import AxiosInstance from "../config/AxiosInstance";

/**
 * Back-office (dashboard) API client for `dbo.table_reservations`.
 *
 * Endpoints are mounted in dev-core under `/api/table-reservations` and are
 * scoped server-side to `req.user.companyId` (JWT). The shared `AxiosInstance`
 * already injects the bearer token.
 *
 * Surface: list + detail + edit + create. The create path tags the row with
 * source = `Dashboard` server-side (the FE does NOT send source); status
 * defaults to Confirmed. Bookings can also originate from POS devices and
 * integration partners — those rows arrive via their own backend paths.
 */

/** Mirrors `dev-core/src/common/enums/reservation.enums.ts#ReservationStatus`. */
export enum ReservationStatus {
  Pending = 0,
  Confirmed = 1,
  Seated = 2,
  Cancelled = 3,
  NoShow = 4,
  Completed = 5,
}

/** Mirrors `dev-core/src/common/enums/reservation.enums.ts#ReservationSource`. */
export enum ReservationSource {
  POS = 0,
  Website = 1,
  Phone = 2,
  WalkIn = 3,
  Integration = 4,
  /** Manually created from this back-office UI (host/manager). */
  Dashboard = 5,
}

export const RESERVATION_STATUS_LABELS: Record<ReservationStatus, string> = {
  [ReservationStatus.Pending]: "Pending",
  [ReservationStatus.Confirmed]: "Confirmed",
  [ReservationStatus.Seated]: "Seated",
  [ReservationStatus.Cancelled]: "Cancelled",
  [ReservationStatus.NoShow]: "No Show",
  [ReservationStatus.Completed]: "Completed",
};

export const RESERVATION_SOURCE_LABELS: Record<ReservationSource, string> = {
  [ReservationSource.POS]: "POS",
  [ReservationSource.Website]: "Website",
  [ReservationSource.Phone]: "Phone",
  [ReservationSource.WalkIn]: "Walk-in",
  [ReservationSource.Integration]: "Integration",
  [ReservationSource.Dashboard]: "Dashboard",
};

export interface TableReservation {
  id: string;
  companyId: string;
  locationId: string;
  floorPlanDetailId: string | null;
  reservationNumber: string;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  partySize: number;
  reservedFrom: string;
  reservedTo: string;
  status: ReservationStatus;
  source: ReservationSource;
  integrationClientId: string | null;
  notes: string | null;
  tableAssignedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  isDeleted: boolean;
}

export interface TableReservationListParams {
  page?: number;
  limit?: number;
  locationId?: string;
  status?: ReservationStatus | number;
  source?: ReservationSource | number;
  /** ISO-8601 datetime; lower bound on `reservedFrom`. */
  dateFrom?: string;
  /** ISO-8601 datetime; upper bound on `reservedFrom`. */
  dateTo?: string;
  /** Substring matched against reservationNumber + customerName (ILIKE). */
  search?: string;
}

export interface TableReservationListResponse {
  data: TableReservation[];
  count: number;
}

export interface UpdateTableReservationPayload {
  status?: ReservationStatus;
  floorPlanDetailId?: string | null;
  partySize?: number;
  customerPhone?: string | null;
  customerEmail?: string | null;
  notes?: string | null;
}

/**
 * POST /api/table-reservations payload (back-office dashboard create).
 *
 * - `source` is intentionally NOT settable here — the back end fixes it to
 *   `ReservationSource.Dashboard`.
 * - `status` is also server-controlled (defaults to Confirmed).
 * - `companyId` is taken from the JWT.
 * - `reservedFrom` is an ISO-8601 string; the back end derives `reservedTo`
 *   from the location's `default_duration_minutes`.
 * - The same validation rules as POS create apply (paused / blocked period /
 *   capacity / operating hours / booking window) — the BE returns 400 with
 *   a human-readable `message` that the form surfaces verbatim.
 */
export interface CreateTableReservationPayload {
  locationId: string;
  partySize: number;
  reservedFrom: string;
  customerName: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  floorPlanDetailId?: string | null;
  notes?: string | null;
}

export const getTableReservations = async (
  params: TableReservationListParams = {}
): Promise<TableReservationListResponse> => {
  try {
    // Drop empty / nullish values so axios doesn't serialise `?status=` / `?search=`
    // into the URL — that confuses the back-end's "no filter" detection.
    const cleaned: Record<string, string | number> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof value === "string" && value.trim() === "") return;
      cleaned[key] = value as string | number;
    });
    const { data } = await AxiosInstance.get("/api/table-reservations", {
      params: cleaned,
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const getTableReservationById = async (
  id: string
): Promise<TableReservation> => {
  try {
    const { data } = await AxiosInstance.get(`/api/table-reservations/${id}`);
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateTableReservation = async (
  id: string,
  payload: UpdateTableReservationPayload
): Promise<TableReservation> => {
  try {
    const { data } = await AxiosInstance.patch(
      `/api/table-reservations/${id}`,
      payload
    );
    return data;
  } catch (error) {
    throw error;
  }
};

export const createTableReservation = async (
  payload: CreateTableReservationPayload
): Promise<TableReservation> => {
  try {
    // Trim string fields to avoid sending whitespace-only values, and
    // collapse empty strings on nullable fields to undefined so the BE's
    // `forbidNonWhitelisted` pipe doesn't re-validate them. The form layer
    // already enforces required fields; this is a belt-and-braces guard.
    const cleaned: CreateTableReservationPayload = {
      locationId: payload.locationId,
      partySize: payload.partySize,
      reservedFrom: payload.reservedFrom,
      customerName: payload.customerName.trim(),
    };
    if (payload.customerPhone && payload.customerPhone.trim()) {
      cleaned.customerPhone = payload.customerPhone.trim();
    }
    if (payload.customerEmail && payload.customerEmail.trim()) {
      cleaned.customerEmail = payload.customerEmail.trim();
    }
    if (payload.floorPlanDetailId) {
      cleaned.floorPlanDetailId = payload.floorPlanDetailId;
    }
    if (payload.notes && payload.notes.trim()) {
      cleaned.notes = payload.notes.trim();
    }
    const { data } = await AxiosInstance.post(
      "/api/table-reservations",
      cleaned
    );
    return data;
  } catch (error) {
    throw error;
  }
};
